import type { Estate } from '@/features/estates-property/api/estate';
import type { EstateValidationSchemaType } from '../schemas/estate-validation';
import { updateEstateAreas } from '@/features/estates-property/api/estate-areas';
import { updateEstateDetails } from '@/features/estates-property/api/estate-details';
import { updateEstateEnergy } from '@/features/estates-property/api/estate-energy';
import { updateEstateRating } from '@/features/estates-property/api/estate-rating';
import { PROPERTY_LEVELS } from '@/constants/property-level';
import type { MutationConfig } from '@/lib/react-query';
import { useMutation } from '@tanstack/react-query';
import { editEstate } from '@/features/estates/api/estates';

type UseUpdateEstateInfo = {
  mutationConfig?: MutationConfig<typeof updateEstateInfo>;
};

export interface UpdateEstateInfo {
  estate: Estate;
  updatedData: EstateValidationSchemaType;
}

const optionalString = (value: string | null | undefined) => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
};

const updateEstateInfo = async ({ estate, updatedData }: UpdateEstateInfo) => {
  const details = estate.estateDetails;

  const allPromises = await Promise.allSettled([
    editEstate({
      id: estate.id,
      estateData: {
        marketType: String(updatedData.estateDetails.marketType),
        propertyType: String(updatedData.estateDetails.propertyType),
        name: updatedData.name,
        propertyLevel: PROPERTY_LEVELS.OBJECT,
        tenantId: estate.tenantId ?? '',
      },
      addressData: {
        ...updatedData.address,
      },
    }),
    updateEstateAreas({
      id: estate.id,
      livingArea: updatedData.estateAreasRooms.livingArea,
      totalNumberOfRooms: updatedData.estateAreasRooms.totalNumberOfRooms,
      numberOfBathrooms: updatedData.estateAreasRooms.numberOfBathrooms,
      balconyArea: updatedData.estateAreasRooms.balconyArea,
      gardenArea: updatedData.estateAreasRooms.gardenArea,
      floor: updatedData.estateAreasRooms.floor ?? undefined,
      hasElevator: updatedData.estateAreasRooms.hasElevator ?? undefined,
      ...(typeof updatedData.estateAreasRooms?.plotArea === 'number'
        ? { plotArea: updatedData.estateAreasRooms?.plotArea }
        : {}),
    }),
    updateEstateDetails({
      id: estate.id,
      externalId: optionalString(details?.externalId),
      externalLink: optionalString(details?.externalLink),
      constructionType: optionalString(details?.constructionType),
      usageType: details?.usageType ?? 'residential',
      condition: optionalString(details?.condition),
      ...(details?.heatingType?.length ? { heatingType: details.heatingType } : {}),
      heatGeneration: optionalString(details?.heatGeneration),
      typeOfGlass: optionalString(details?.typeOfGlass),
      flooring: optionalString(details?.flooring),
      building: optionalString(details?.building),
      buildingYear: String(updatedData.estateDetails.buildingYear),
      marketType: String(updatedData.estateDetails.marketType),
      propertyType: String(updatedData.estateDetails.propertyType),
    }),
    updateEstateEnergy({
      id: estate.id,
      issuedOn: estate.estateEnergyDetails?.issuedOn ?? '',
      validUntil: estate.estateEnergyDetails?.validUntil ?? '',
      hwbClass: updatedData.estateEnergyDetails.hwbClass,
      hwbRating: estate.estateEnergyDetails?.hwbRating ?? '',
      fgeeClass: estate.estateEnergyDetails?.fgeeClass ?? '',
      fgeeRating: estate.estateEnergyDetails?.fgeeRating ?? '',
    }),
    updateEstateRating({
      id: estate.id,
      bathroomCondition: updatedData.estateRatings?.bathroomCondition ?? 0,
      bathroomQuality: updatedData.estateRatings?.bathroomQuality ?? 0,
      flooringCondition: updatedData.estateRatings.flooringCondition ?? 0,
      flooringQuality: updatedData.estateRatings?.flooringQuality ?? 0,
      kitchenCondition: updatedData.estateRatings?.kitchenCondition ?? 0,
      kitchenQuality: updatedData.estateRatings?.kitchenQuality ?? 0,
      windowsCondition: updatedData.estateRatings?.windowsCondition ?? 0,
      windowsQuality: updatedData.estateRatings?.windowsQuality ?? 0,
    }),
  ]);

  const rejectedPromise = allPromises.find(({ status }) => status === 'rejected');

  if (rejectedPromise) {
    throw Error((rejectedPromise as PromiseRejectedResult).reason);
  } else {
    return true;
  }
};

export const useUpdateEstateInfo = ({ mutationConfig }: UseUpdateEstateInfo) => {
  return useMutation({
    ...mutationConfig,
    mutationFn: updateEstateInfo,
  });
};
